import React, { useState, Children } from 'react';
import { Container } from './ui/Container';
import { Button } from './ui/Button';
import { Download, Phone, Send } from 'lucide-react';
interface OfferCardProps {
  title: string;
  image: string;
  pdfLink: string;
}
function OfferCard({
  title,
  image,
  pdfLink
}: OfferCardProps) {
  return <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col h-full border border-gray-100" data-id="element-377">
      <div className="relative h-64 overflow-hidden bg-gray-100" data-id="element-378">
        <img src={image} alt={title} className="w-full h-full object-cover" data-id="element-379" />
      </div>

      <div className="p-6 flex flex-col flex-grow border-t border-gray-100" data-id="element-380">
        <h3 className="text-xl font-bold text-nav mb-6 font-heading text-center" data-id="element-381">
          {title}
        </h3>

        <div className="mt-auto space-y-3" data-id="element-382">
          <a href={pdfLink} target="_blank" rel="noopener noreferrer" className="block w-full" data-id="element-383">
            <Button variant="primary" fullWidth className="flex items-center justify-center gap-2" data-id="element-384">
              <Download className="w-4 h-4" aria-hidden="true" data-id="element-385" />
              Download / Print PDF
            </Button>
          </a>
          <a href="tel:8472128045" className="block w-full" data-id="element-386">
            <Button variant="secondary" fullWidth className="!text-nav !border-nav hover:!bg-nav hover:!text-white flex items-center justify-center gap-2" data-id="element-387">
              <Phone className="w-4 h-4" aria-hidden="true" data-id="element-388" />
              Call to Get Started
            </Button>
          </a>
        </div>
      </div>
    </div>;
}
interface FormState {
  name: string;
  email: string;
  phone: string;
  program: string;
  message: string;
}
export function OffersSection() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const programs = ["Children's Classes", 'Children / Adult Classes', 'Cardio Kickboxing', 'FIT KIDS Program', 'Advanced Classes', 'Sparring', 'Demo Team', 'Community Engagement'];
  const offers = [{
    title: 'One Week FREE Trial',
    image: 'https://cdn.magicpatterns.com/uploads/3gNiHt7g3E9MQoqeCzaxPG/One-Week-Free-Trial-at-Dragon-Martial-Arts.jpg',
    pdfLink: 'https://dragonmartialarts-rl.com/wp-content/uploads/2019/06/One-Week-FREE-trial_New.pdf'
  }, {
    title: 'Cardio Kickboxing Special',
    image: 'https://cdn.magicpatterns.com/uploads/fsFF1VPPDMa1aTMW9v5q1E/Cardio-Kickboxing-at-Dragon-Martial-Arts.jpg',
    pdfLink: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/CARDIO-KICKBOXING_1.pdf'
  }, {
    title: 'Dragon Martial Arts Special',
    image: 'https://cdn.magicpatterns.com/uploads/vcYhYKkTvM1hty9cPstR2m/Martial-Arts-Specials.jpg',
    pdfLink: 'https://dragonmartialarts-rl.com/wp-content/uploads/2023/10/DRAGON-MARTIAL-ARTS-OF-ROUND-LAKE_1.pdf'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <section className="py-20 bg-gray-100 border-t border-gray-200" data-id="element-389">
      <Container data-id="element-390">
        {/* Heading */}
        <div className="text-center mb-16" data-id="element-391">
          <h2 className="text-3xl md:text-4xl font-extrabold text-nav mb-4 uppercase tracking-tight font-heading" data-id="element-392">
            Claim Your Special Offer
          </h2>
          <div className="w-20 h-1 bg-accent mx-auto mb-6" data-id="element-393"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-394">
            Start your martial arts journey today
          </p>
        </div>

        {/* Offer Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16" data-id="element-395">
          {offers.map((offer, index) => <OfferCard key={index} {...offer} data-id="element-396" />)}
        </div>

        {/* Request Info Form */}
        <div className="max-w-3xl mx-auto" data-id="element-397">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100" data-id="element-398">
            {/* Form Header */}
            <div className="bg-nav px-8 py-6" data-id="element-399">
              <h3 className="text-2xl font-extrabold text-white font-heading uppercase tracking-tight" data-id="element-400">
                Request Information
              </h3>
              <p className="text-gray-400 mt-1 text-sm" data-id="element-401">
                Fill out the form below and we'll be in touch.
              </p>
            </div>

            {submitted ? <div className="px-8 py-16 text-center" data-id="element-402">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-403">
                  <Send className="w-8 h-8 text-green-600" aria-hidden="true" data-id="element-404" />
                </div>
                <h4 className="text-2xl font-bold text-nav font-heading mb-2" data-id="element-405">
                  Thank You!
                </h4>
                <p className="text-gray-600 mb-6" data-id="element-406">
                  Your message has been received. We look forward to hearing
                  from you.
                </p>
                <p className="text-gray-500 text-sm" data-id="element-407">
                  Or call us directly:{' '}
                  <a href="tel:8472128045" className="text-link font-bold hover:text-accent" data-id="element-408">
                    (847) 212-8045
                  </a>
                </p>
              </div> : <form onSubmit={handleSubmit} className="px-8 py-8 space-y-6" data-id="element-409">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-410">
                  {/* Name */}
                  <div data-id="element-411">
                    <label htmlFor="name" className="block text-sm font-bold text-nav mb-2 font-heading uppercase tracking-wide" data-id="element-412">
                      Name <span className="text-accent" data-id="element-413">*</span>
                    </label>
                    <input id="name" name="name" type="text" required value={form.name} onChange={handleChange} placeholder="Your full name" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-414" />
                  </div>

                  {/* Email */}
                  <div data-id="element-415">
                    <label htmlFor="email" className="block text-sm font-bold text-nav mb-2 font-heading uppercase tracking-wide" data-id="element-416">
                      Email <span className="text-accent" data-id="element-417">*</span>
                    </label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="your@email.com" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-418" />
                  </div>

                  {/* Phone */}
                  <div data-id="element-419">
                    <label htmlFor="phone" className="block text-sm font-bold text-nav mb-2 font-heading uppercase tracking-wide" data-id="element-420">
                      Phone
                    </label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-421" />
                  </div>

                  {/* Program */}
                  <div data-id="element-422">
                    <label htmlFor="program" className="block text-sm font-bold text-nav mb-2 font-heading uppercase tracking-wide" data-id="element-423">
                      Interested Program
                    </label>
                    <select id="program" name="program" value={form.program} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-424">
                      <option value="" data-id="element-425">Select a program…</option>
                      {programs.map(p => <option key={p} value={p} data-id="element-426">
                          {p}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div data-id="element-427">
                  <label htmlFor="message" className="block text-sm font-bold text-nav mb-2 font-heading uppercase tracking-wide" data-id="element-428">
                    Message
                  </label>
                  <textarea id="message" name="message" rows={4} value={form.message} onChange={handleChange} placeholder="Any questions or details you'd like to share…" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors resize-none" data-id="element-429" />
                </div>

                {/* Submit */}
                <div className="flex flex-col sm:flex-row items-center gap-4 pt-2" data-id="element-430">
                  <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-431">
                    <Send className="w-4 h-4" aria-hidden="true" data-id="element-432" />
                    Request Information
                  </Button>
                  <span className="text-gray-500 text-sm" data-id="element-433">
                    Or call{' '}
                    <a href="tel:8472128045" className="text-link font-bold hover:text-accent" data-id="element-434">
                      (847) 212-8045
                    </a>
                  </span>
                </div>
              </form>}
          </div>
        </div>
      </Container>
    </section>;
}